import { useState, useEffect, useRef, useMemo } from 'react'
import { useSearchParams } from 'react-router-dom'
import { pterodactylAPI } from '../lib/api'
import { Terminal, Play, Square, RotateCcw, Send, ChevronRight, Zap, Type, Sliders, X, Loader2 } from 'lucide-react'

// ── Preset types ──────────────────────────────────────────────────────────────
// instant  → sends cmd directly
// fill     → puts cmd in the text input
// form     → opens builder with fields; template uses {fieldName} placeholders
//   field types: text | select | number
//   select options: ['val'] or [{value, label}]

const PRESETS = {
  quake3: [
    { label: 'Info', cmds: [
      { label: 'Status',      cmd: 'status',      type: 'instant' },
      { label: 'Server Info', cmd: 'serverinfo',  type: 'instant' },
      { label: 'System Info', cmd: 'systeminfo',  type: 'instant' },
      { label: 'Mem Info',    cmd: 'meminfo',     type: 'instant' },
      { label: 'Cmd List',    cmd: 'cmdlist',     type: 'instant' },
      { label: 'Cvar List',   cmd: 'cvarlist',    type: 'instant' },
      { label: 'Dumpuser', type: 'form', template: 'dumpuser {name}',
        fields: [{ name: 'name', label: 'Spelernaam', type: 'text', placeholder: '' }],
      },
    ]},
    { label: 'Map', cmds: [
      { label: 'Map Restart', cmd: 'map_restart', type: 'instant' },
      { label: 'Next Map',    cmd: 'map_rotate',  type: 'instant' },
      { label: 'Map wisselen', type: 'form', template: 'map {map}',
        fields: [{ name: 'map', label: 'Map', type: 'select', options: [
          'q3dm1','q3dm2','q3dm3','q3dm4','q3dm5','q3dm6','q3dm7',
          'q3dm8','q3dm9','q3dm10','q3dm11','q3dm12','q3dm13','q3dm14',
          'q3dm15','q3dm16','q3dm17','q3dm18','q3dm19',
          'q3tourney1','q3tourney2','q3tourney3','q3tourney4','q3tourney5','q3tourney6',
          'q3ctf1','q3ctf2','q3ctf3','q3ctf4',
        ]}],
      },
      { label: 'Exec cfg', type: 'form', template: 'exec {file}',
        fields: [{ name: 'file', label: 'Config bestand', type: 'text', placeholder: 'server.cfg' }],
      },
    ]},
    { label: 'Gametype', cmds: [
      { label: 'FFA (0)',  cmd: 'g_gametype 0', type: 'instant' },
      { label: 'Duel (1)', cmd: 'g_gametype 1', type: 'instant' },
      { label: 'TDM (3)',  cmd: 'g_gametype 3', type: 'instant' },
      { label: 'CTF (4)',  cmd: 'g_gametype 4', type: 'instant' },
    ]},
    { label: 'Limieten', cmds: [
      { label: 'Fraglimit', type: 'form', template: 'fraglimit {n}',
        fields: [{ name: 'n', label: 'Fraglimit', type: 'number', min: 0, max: 200, default: 30 }],
      },
      { label: 'Timelimit', type: 'form', template: 'timelimit {n}',
        fields: [{ name: 'n', label: 'Minuten', type: 'number', min: 0, max: 60, default: 15 }],
      },
      { label: 'Capturelimit', type: 'form', template: 'capturelimit {n}',
        fields: [{ name: 'n', label: 'Captures (CTF)', type: 'number', min: 0, max: 50, default: 8 }],
      },
      { label: 'Max spelers', type: 'form', template: 'sv_maxclients {n}',
        fields: [{ name: 'n', label: 'Max clients', type: 'number', min: 1, max: 64, default: 16 }],
      },
    ]},
    { label: 'Instellingen', cmds: [
      { label: 'Warmup', type: 'form', template: 'g_warmup {n}',
        fields: [{ name: 'n', label: 'Seconden', type: 'number', min: 0, max: 120, default: 20 }],
      },
      { label: 'Gravity', type: 'form', template: 'g_gravity {n}',
        fields: [{ name: 'n', label: 'Zwaartekracht', type: 'number', min: 0, max: 2000, default: 800 }],
      },
      { label: 'Friendly Fire', type: 'form', template: 'g_friendlyFire {val}',
        fields: [{ name: 'val', label: 'Team damage', type: 'select', options: [{ value: '0', label: 'Uit' }, { value: '1', label: 'Aan' }] }],
      },
      { label: 'Allow Vote', type: 'form', template: 'g_allowvote {val}',
        fields: [{ name: 'val', label: 'Spelers mogen stemmen', type: 'select', options: [{ value: '1', label: 'Aan' }, { value: '0', label: 'Uit' }] }],
      },
      { label: 'Inactivity', type: 'form', template: 'g_inactivity {n}',
        fields: [{ name: 'n', label: 'Seconden (0=uit)', type: 'number', min: 0, max: 600, default: 0 }],
      },
      { label: 'MOTD', type: 'form', template: 'g_motd {msg}',
        fields: [{ name: 'msg', label: 'Welkomstbericht', type: 'text', placeholder: 'Welkom!' }],
      },
      { label: 'Set CVar', type: 'form', template: 'set {cvar} {value}',
        fields: [
          { name: 'cvar',  label: 'CVar naam', type: 'text', placeholder: 'sv_hostname' },
          { name: 'value', label: 'Waarde',    type: 'text', placeholder: '' },
        ],
      },
    ]},
    { label: 'Spelers', cmds: [
      { label: 'Say', type: 'form', template: 'say {msg}',
        fields: [{ name: 'msg', label: 'Bericht', type: 'text', placeholder: '' }],
      },
      { label: 'Kick (naam)', type: 'form', template: 'kick {name}',
        fields: [{ name: 'name', label: 'Naam', type: 'text', placeholder: '' }],
      },
      { label: 'Kick (slot)', type: 'form', template: 'clientkick {id}',
        fields: [{ name: 'id', label: 'Client #', type: 'number', min: 0, max: 64, default: 0 }],
      },
      { label: 'Add Bot', type: 'form', template: 'addbot {bot} {skill}',
        fields: [
          { name: 'bot', label: 'Bot', type: 'select', options: [
            'Keel','Orbb','Grunt','Slash','Sarge','Daemia','Mynx','Anarki',
            'Biker','Bitterman','Bones','Hunter','Klesk','Lucy','Major',
            'Phobos','Razor','Stripe','Tank Jr.','Uriel','Visor','Xaero',
          ]},
          { name: 'skill', label: 'Skill', type: 'select', options: [
            { value: '1', label: '1 – Noob' },
            { value: '2', label: '2 – Easy' },
            { value: '3', label: '3 – Medium' },
            { value: '4', label: '4 – Hard' },
            { value: '5', label: '5 – Nightmare' },
          ]},
        ],
      },
    ]},
  ],

  mohaa: [
    { label: 'Info', cmds: [
      { label: 'Status',      cmd: 'status',       type: 'instant' },
      { label: 'Players',     cmd: 'players',      type: 'instant' },
      { label: 'Map Restart', cmd: 'map_restart',  type: 'instant' },
      { label: 'Restart',     cmd: 'restart',      type: 'instant' },
      { label: 'Quit',        cmd: 'quit',         type: 'instant' },
    ]},
    { label: 'Map', cmds: [
      { label: 'Map wisselen (DM)', type: 'form', template: 'map {map}',
        fields: [{ name: 'map', label: 'Map', type: 'select', options: [
          'dm/mohdm1','dm/mohdm2','dm/mohdm3','dm/mohdm4','dm/mohdm5','dm/mohdm6','dm/mohdm7',
          'obj/obj_team1','obj/obj_team2','obj/obj_team3','obj/obj_team4',
          'obj/obj_team5','obj/obj_team6','obj/obj_team7','obj/obj_team8','obj/obj_team9',
          'team/moh_team_airfield','team/moh_team_base','team/moh_team_beach',
          'team/moh_team_bridge','team/moh_team_dawnville','team/moh_team_depot',
          'team/moh_team_desert','team/moh_team_face','team/moh_team_harbor',
          'team/moh_team_ruins','team/moh_team_snowy','team/moh_team_trenchraid',
          // Spearhead
          'mp/mp_ardennes_tow','mp/mp_bahnhof_dm','mp/mp_bazaar_dm','mp/mp_berlin_tow',
          'mp/mp_brest_dm','mp/mp_gewitter_dm','mp/mp_Holland_dm','mp/mp_malta_dm',
          'mp/mp_stadt_dm','mp/mp_verschneit_dm',
          // Breakthrough
          'mp/mp_anzio_lib','mp/mp_bizertefort_obj','mp/mp_bizerteharbor_lib',
          'mp/mp_bologna_obj','mp/mp_castello_obj','mp/mp_kasserline_tow',
          'mp/mp_montebattaglia_tow','mp/mp_montecassino_tow','mp/mp_palermo_obj','mp/mp_tunisia_lib',
        ]}],
      },
      { label: 'Exec cfg', type: 'form', template: 'exec {file}',
        fields: [{ name: 'file', label: 'Config bestand', type: 'text', placeholder: 'server.cfg' }],
      },
    ]},
    { label: 'Gametype', cmds: [
      { label: 'FFA (1)',       cmd: 'g_gametype 1', type: 'instant' },
      { label: 'Team (2)',      cmd: 'g_gametype 2', type: 'instant' },
      { label: 'Round (3)',     cmd: 'g_gametype 3', type: 'instant' },
      { label: 'Objective (4)', cmd: 'g_gametype 4', type: 'instant' },
    ]},
    { label: 'Limieten', cmds: [
      { label: 'Fraglimit', type: 'form', template: 'fraglimit {n}',
        fields: [{ name: 'n', label: 'Fraglimit', type: 'number', min: 0, max: 200, default: 30 }],
      },
      { label: 'Timelimit', type: 'form', template: 'timelimit {n}',
        fields: [{ name: 'n', label: 'Minuten', type: 'number', min: 0, max: 60, default: 15 }],
      },
      { label: 'Roundlimit', type: 'form', template: 'roundlimit {n}',
        fields: [{ name: 'n', label: 'Rounds', type: 'number', min: 0, max: 50, default: 10 }],
      },
      { label: 'Max spelers', type: 'form', template: 'set sv_maxclients {n}',
        fields: [{ name: 'n', label: 'Max clients', type: 'number', min: 1, max: 64, default: 16 }],
      },
    ]},
    { label: 'Instellingen', cmds: [
      { label: 'Friendly Fire', type: 'form', template: 'set g_teamdamage {val}',
        fields: [{ name: 'val', label: 'Team damage', type: 'select', options: [{ value: '0', label: 'Uit' }, { value: '1', label: 'Aan' }] }],
      },
      { label: 'Server wachtwoord', type: 'form', template: 'set g_password {pw}',
        fields: [{ name: 'pw', label: 'Wachtwoord (leeg = open)', type: 'text', placeholder: '' }],
      },
      { label: 'Allow Vote', type: 'form', template: 'set g_allowVote {val}',
        fields: [{ name: 'val', label: 'Stemmen', type: 'select', options: [{ value: '1', label: 'Aan' }, { value: '0', label: 'Uit' }] }],
      },
      { label: 'Kick bij hoge ping', type: 'form', template: 'set sv_kickping {n}',
        fields: [{ name: 'n', label: 'Max ping (0=uit)', type: 'number', min: 0, max: 999, default: 0 }],
      },
      { label: 'Inactivity', type: 'form', template: 'set g_inactivity {n}',
        fields: [{ name: 'n', label: 'Seconden (0=uit)', type: 'number', min: 0, max: 600, default: 0 }],
      },
      { label: 'Set CVar', type: 'form', template: 'set {cvar} {value}',
        fields: [
          { name: 'cvar',  label: 'CVar naam', type: 'text', placeholder: 'sv_hostname' },
          { name: 'value', label: 'Waarde',    type: 'text', placeholder: '' },
        ],
      },
    ]},
    { label: 'Spelers', cmds: [
      { label: 'Say', type: 'form', template: 'say {msg}',
        fields: [{ name: 'msg', label: 'Bericht', type: 'text', placeholder: '' }],
      },
      { label: 'Kick (naam)', type: 'form', template: 'kick {name}',
        fields: [{ name: 'name', label: 'Naam', type: 'text', placeholder: '' }],
      },
      { label: 'Kick (slot)', type: 'form', template: 'clientkick {id}',
        fields: [{ name: 'id', label: 'Client #', type: 'number', min: 0, max: 64, default: 0 }],
      },
      { label: 'Kick all', cmd: 'kick all', type: 'instant' },
    ]},
  ],

  ut2004: [
    { label: 'Admin', cmds: [
      { label: 'Player List',  cmd: 'Admin PlayerList',   type: 'instant' },
      { label: 'Restart Map',  cmd: 'Admin RestartMap',   type: 'instant' },
      { label: 'Next Map',     cmd: 'Admin NextMap',      type: 'instant' },
      { label: 'Kill Bots',    cmd: 'Admin KillBots',     type: 'instant' },
      { label: 'Pause',        cmd: 'Admin Pause',        type: 'instant' },
      { label: 'Exit',         cmd: 'Admin Exit',         type: 'instant' },
      { label: 'Admin Login', type: 'form', template: 'adminlogin {password}',
        fields: [{ name: 'password', label: 'Wachtwoord', type: 'text', placeholder: '' }],
      },
    ]},
    { label: 'Map', cmds: [
      { label: 'Change Map', type: 'form', template: 'Admin ServerTravel {map}?game={game}',
        fields: [
          { name: 'map', label: 'Map', type: 'select', options: [
            // DM
            'DM-Antalus','DM-Asbestos','DM-Compressed','DM-Curse3','DM-Flux2','DM-Gael',
            'DM-Inferno','DM-Insidious','DM-Leviathan','DM-Oceanic','DM-Phobos2','DM-Plunge',
            'DM-Serpentine','DM-TokaraForest','DM-TrainingDay',
            'DM-1on1-Albatross','DM-1on1-Idoma','DM-1on1-Irondust','DM-1on1-Roughinery',
            'DM-1on1-Spirit','DM-1on1-Squader','DM-1on1-Trite',
            // CTF
            'CTF-Chrome','CTF-Citadel','CTF-December','CTF-Face3','CTF-Geothermal',
            'CTF-Lostfaith','CTF-Magma','CTF-Maul','CTF-Orbital2',
            // ONS
            'ONS-ArcticStronghold','ONS-Crossfire','ONS-Dawn','ONS-Dria','ONS-FrostBite',
            'ONS-Primeval','ONS-RedPlanet','ONS-Severance','ONS-Torlan',
            // AS
            'AS-Convoy','AS-FallenCity','AS-Glacier','AS-Junkyard','AS-MotherShip','AS-RobotFactory',
            // BR
            'BR-Anubis','BR-Bifrost','BR-Disclosure','BR-IceFields','BR-Skyline','BR-Slaughterhouse','BR-TwinTombs',
            // DOM
            'DOM-Core','DOM-OutRigger','DOM-Ruination','DOM-ScorchedEarth','DOM-SepukkuGorge','DOM-Suntemple',
          ]},
          { name: 'game', label: 'Gametype', type: 'select', options: [
            { value: 'XGame.xDeathMatch',           label: 'Deathmatch' },
            { value: 'XGame.xTeamGame',             label: 'Team Deathmatch' },
            { value: 'XGame.xCTFGame',              label: 'Capture The Flag' },
            { value: 'Onslaught.ONSOnslaughtGame',  label: 'Onslaught' },
            { value: 'UT2k4Assault.ASGameInfo',     label: 'Assault' },
            { value: 'XGame.xBombingRun',           label: 'Bombing Run' },
            { value: 'XGame.xDoubleDom',            label: 'Double Domination' },
            { value: 'SkaarjPack.Invasion',         label: 'Invasion' },
            { value: 'BonusPack.xLastManStandingGame', label: 'Last Man Standing' },
            { value: 'BonusPack.xMutantGame',       label: 'Mutant' },
          ]},
        ],
      },
      { label: 'Quick Map', type: 'form', template: 'Admin SwitchLevel {map}',
        fields: [{ name: 'map', label: 'Map naam', type: 'text', placeholder: 'DM-Gael' }],
      },
    ]},
    { label: 'Limieten', cmds: [
      { label: 'Timelimit', type: 'form', template: 'Admin AdminTimeLimit {n}',
        fields: [{ name: 'n', label: 'Minuten', type: 'number', min: 0, max: 60, default: 20 }],
      },
      { label: 'Fraglimit', type: 'form', template: 'Admin AdminFragLimit {n}',
        fields: [{ name: 'n', label: 'Fraglimit', type: 'number', min: 0, max: 200, default: 30 }],
      },
      { label: 'Teamscore', type: 'form', template: 'Admin AdminTeamScore {n}',
        fields: [{ name: 'n', label: 'Teamscore', type: 'number', min: 0, max: 100, default: 5 }],
      },
      { label: 'Max Players', type: 'form', template: 'Admin Set Engine.GameInfo MaxPlayers {n}',
        fields: [{ name: 'n', label: 'Max spelers', type: 'number', min: 1, max: 32, default: 16 }],
      },
      { label: 'Min Players (bots)', type: 'form', template: 'Admin Set UnrealGame.UnrealMPGameInfo MinPlayers {n}',
        fields: [{ name: 'n', label: 'Min spelers (bots vullen aan)', type: 'number', min: 0, max: 16, default: 0 }],
      },
    ]},
    { label: 'Instellingen', cmds: [
      { label: 'Game Speed', type: 'form', template: 'Admin Set Engine.LevelInfo TimeDilation {speed}',
        fields: [{ name: 'speed', label: 'Snelheid', type: 'select', options: [
          { value: '0.5',  label: '50%  – Half speed' },
          { value: '0.75', label: '75%  – Slow' },
          { value: '1.0',  label: '100% – Normaal' },
          { value: '1.5',  label: '150% – Snel' },
          { value: '2.0',  label: '200% – Dubbel' },
        ]}],
      },
      { label: 'Bot Difficulty', type: 'form', template: 'Admin Set Engine.GameInfo GameDifficulty {diff}',
        fields: [{ name: 'diff', label: 'Moeilijkheid', type: 'select', options: [
          { value: '0', label: '0 – Novice' },
          { value: '1', label: '1 – Average' },
          { value: '2', label: '2 – Experienced' },
          { value: '3', label: '3 – Skilled' },
          { value: '4', label: '4 – Adept' },
          { value: '5', label: '5 – Masterful' },
          { value: '6', label: '6 – Inhuman' },
          { value: '7', label: '7 – Godlike' },
        ]}],
      },
      { label: 'Exec', type: 'form', template: 'Admin Exec {file}',
        fields: [{ name: 'file', label: 'Bestand (System/)', type: 'text', placeholder: 'server.ini' }],
      },
    ]},
    { label: 'Spelers', cmds: [
      { label: 'AdminSay', type: 'form', template: 'Admin AdminSay {msg}',
        fields: [{ name: 'msg', label: 'Bericht', type: 'text', placeholder: '' }],
      },
      { label: 'Kick', type: 'form', template: 'Admin Kick {player}',
        fields: [{ name: 'player', label: 'Spelernaam', type: 'text', placeholder: '' }],
      },
      { label: 'KickBan', type: 'form', template: 'Admin KickBan {player}',
        fields: [{ name: 'player', label: 'Spelernaam', type: 'text', placeholder: '' }],
      },
      { label: 'Add Bots', type: 'form', template: 'Admin AddBots {n}',
        fields: [{ name: 'n', label: 'Aantal bots', type: 'number', min: 1, max: 16, default: 1 }],
      },
      { label: 'Add Named Bot', type: 'form', template: 'Admin AddNamedBot {name}',
        fields: [{ name: 'name', label: 'Bot naam', type: 'text', placeholder: 'Xan' }],
      },
    ]},
  ],

  openra: [
    { label: 'Server', cmds: [
      { label: 'Help',   cmd: '/help',   type: 'instant' },
      { label: 'Status', cmd: '/status', type: 'instant' },
      { label: 'Pause',  cmd: '!pause',  type: 'instant' },
      { label: 'Resume', cmd: '!resume', type: 'instant' },
      { label: 'Stop',   cmd: '!stop',   type: 'instant' },
      { label: 'Start',  cmd: '!start',  type: 'instant' },
    ]},
    { label: 'Spelers', cmds: [
      { label: 'Kick', type: 'form', template: '/kick {player}',
        fields: [{ name: 'player', label: 'Spelernaam', type: 'text', placeholder: '' }],
      },
      { label: 'Ban', type: 'form', template: '/ban {player}',
        fields: [{ name: 'player', label: 'Spelernaam', type: 'text', placeholder: '' }],
      },
      { label: 'Say', type: 'form', template: '/say {msg}',
        fields: [{ name: 'msg', label: 'Bericht', type: 'text', placeholder: '' }],
      },
      { label: 'Private msg', type: 'form', template: '/psay {player} {msg}',
        fields: [
          { name: 'player', label: 'Spelernaam', type: 'text', placeholder: '' },
          { name: 'msg',    label: 'Bericht',    type: 'text', placeholder: '' },
        ],
      },
    ]},
  ],

  minecraft: [
    { label: 'Server', cmds: [
      { label: 'List Players', cmd: 'list',      type: 'instant' },
      { label: 'Save All',     cmd: 'save-all',  type: 'instant' },
      { label: 'Save On',      cmd: 'save-on',   type: 'instant' },
      { label: 'Save Off',     cmd: 'save-off',  type: 'instant' },
      { label: 'Seed',         cmd: 'seed',      type: 'instant' },
      { label: 'Perf',         cmd: 'perf',      type: 'instant' },
      { label: 'Reload',       cmd: 'reload',    type: 'instant' },
      { label: 'Stop',         cmd: 'stop',      type: 'instant' },
      { label: 'Say', type: 'form', template: 'say {msg}',
        fields: [{ name: 'msg', label: 'Bericht', type: 'text', placeholder: '' }],
      },
    ]},
    { label: 'Spelers', cmds: [
      { label: 'Kick', type: 'form', template: 'kick {player} {reason}',
        fields: [
          { name: 'player', label: 'Speler', type: 'text', placeholder: 'spelersnaam' },
          { name: 'reason', label: 'Reden',  type: 'text', placeholder: 'Gekickt door admin', default: 'Gekickt door admin' },
        ],
      },
      { label: 'Ban (naam)', type: 'form', template: 'ban {player} {reason}',
        fields: [
          { name: 'player', label: 'Speler', type: 'text', placeholder: 'spelersnaam' },
          { name: 'reason', label: 'Reden',  type: 'text', placeholder: '' },
        ],
      },
      { label: 'Ban (IP)', type: 'form', template: 'ban-ip {ip}',
        fields: [{ name: 'ip', label: 'IP adres', type: 'text', placeholder: '192.168.1.x' }],
      },
      { label: 'Pardon (naam)', type: 'form', template: 'pardon {player}',
        fields: [{ name: 'player', label: 'Speler', type: 'text', placeholder: '' }],
      },
      { label: 'Pardon (IP)', type: 'form', template: 'pardon-ip {ip}',
        fields: [{ name: 'ip', label: 'IP adres', type: 'text', placeholder: '' }],
      },
      { label: 'Banlist',     cmd: 'banlist',     type: 'instant' },
      { label: 'Banlist IPs', cmd: 'banlist ips', type: 'instant' },
      { label: 'Op geven', type: 'form', template: 'op {player}',
        fields: [{ name: 'player', label: 'Speler', type: 'text', placeholder: '' }],
      },
      { label: 'Op wegnemen', type: 'form', template: 'deop {player}',
        fields: [{ name: 'player', label: 'Speler', type: 'text', placeholder: '' }],
      },
      { label: 'Gamemode', type: 'form', template: 'gamemode {mode} {player}',
        fields: [
          { name: 'mode', label: 'Modus', type: 'select', options: [
            { value: 'survival',  label: 'Survival' },
            { value: 'creative',  label: 'Creative' },
            { value: 'adventure', label: 'Adventure' },
            { value: 'spectator', label: 'Spectator' },
          ]},
          { name: 'player', label: 'Speler (leeg = iedereen)', type: 'text', placeholder: '' },
        ],
      },
      { label: 'TP → speler', type: 'form', template: 'tp {player} {destination}',
        fields: [
          { name: 'player',      label: 'Speler',      type: 'text', placeholder: 'spelersnaam' },
          { name: 'destination', label: 'Naar (speler)', type: 'text', placeholder: 'speler2' },
        ],
      },
      { label: 'TP → coördinaten', type: 'form', template: 'tp {player} {x} {y} {z}',
        fields: [
          { name: 'player', label: 'Speler (@a = iedereen)', type: 'text', placeholder: 'spelersnaam' },
          { name: 'x', label: 'X', type: 'text', placeholder: '~ of 100' },
          { name: 'y', label: 'Y', type: 'text', placeholder: '~ of 64' },
          { name: 'z', label: 'Z', type: 'text', placeholder: '~ of 200' },
        ],
      },
      { label: 'TP → coords + rotatie', type: 'form', template: 'tp {player} {x} {y} {z} {yaw} {pitch}',
        fields: [
          { name: 'player', label: 'Speler', type: 'text', placeholder: 'spelersnaam' },
          { name: 'x',     label: 'X',      type: 'text', placeholder: '100' },
          { name: 'y',     label: 'Y',      type: 'text', placeholder: '64' },
          { name: 'z',     label: 'Z',      type: 'text', placeholder: '200' },
          { name: 'yaw',   label: 'Yaw (−180..180)', type: 'number', min: -180, max: 180, default: 0 },
          { name: 'pitch', label: 'Pitch (−90..90)',  type: 'number', min: -90,  max: 90,  default: 0 },
        ],
      },
      { label: 'TP facing coords', type: 'form', template: 'tp {player} {x} {y} {z} facing {fx} {fy} {fz}',
        fields: [
          { name: 'player', label: 'Speler',   type: 'text', placeholder: 'spelersnaam' },
          { name: 'x',  label: 'X',  type: 'text', placeholder: '100' },
          { name: 'y',  label: 'Y',  type: 'text', placeholder: '64' },
          { name: 'z',  label: 'Z',  type: 'text', placeholder: '200' },
          { name: 'fx', label: 'Kijk naar X', type: 'text', placeholder: '0' },
          { name: 'fy', label: 'Kijk naar Y', type: 'text', placeholder: '64' },
          { name: 'fz', label: 'Kijk naar Z', type: 'text', placeholder: '0' },
        ],
      },
      { label: 'TP facing entity', type: 'form', template: 'tp {player} {x} {y} {z} facing entity {target} {anchor}',
        fields: [
          { name: 'player', label: 'Speler',   type: 'text', placeholder: 'spelersnaam' },
          { name: 'x',  label: 'X',  type: 'text', placeholder: '100' },
          { name: 'y',  label: 'Y',  type: 'text', placeholder: '64' },
          { name: 'z',  label: 'Z',  type: 'text', placeholder: '200' },
          { name: 'target', label: 'Kijk naar (speler)', type: 'text', placeholder: 'speler2' },
          { name: 'anchor', label: 'Ankerpunt', type: 'select', options: [
            { value: 'eyes', label: 'eyes' },
            { value: 'feet', label: 'feet' },
          ]},
        ],
      },
      { label: 'Spawnpoint', type: 'form', template: 'spawnpoint {player} {x} {y} {z}',
        fields: [
          { name: 'player', label: 'Speler', type: 'text', placeholder: '' },
          { name: 'x', label: 'X', type: 'number', min: -30000000, max: 30000000, default: 0 },
          { name: 'y', label: 'Y', type: 'number', min: -64, max: 320, default: 64 },
          { name: 'z', label: 'Z', type: 'number', min: -30000000, max: 30000000, default: 0 },
        ],
      },
      { label: 'XP geven', type: 'form', template: 'xp add {player} {amount} {type}',
        fields: [
          { name: 'player', label: 'Speler', type: 'text', placeholder: '' },
          { name: 'amount', label: 'Hoeveelheid', type: 'number', min: 1, max: 9999, default: 100 },
          { name: 'type', label: 'Type', type: 'select', options: [
            { value: 'points', label: 'XP Points' },
            { value: 'levels', label: 'Levels' },
          ]},
        ],
      },
      { label: 'Title', type: 'form', template: 'title {player} title {"text":"{msg}"}',
        fields: [
          { name: 'player', label: 'Speler (@a = iedereen)', type: 'text', placeholder: '@a' },
          { name: 'msg',    label: 'Bericht', type: 'text', placeholder: '' },
        ],
      },
      { label: 'Msg (privé)', type: 'form', template: 'msg {player} {msg}',
        fields: [
          { name: 'player', label: 'Speler', type: 'text', placeholder: '' },
          { name: 'msg',    label: 'Bericht', type: 'text', placeholder: '' },
        ],
      },
    ]},
    { label: 'Give', cmds: [
      { label: 'Give Item', type: 'form', template: 'give {player} {item} {amount}',
        fields: [
          { name: 'player', label: 'Speler', type: 'text', placeholder: 'spelersnaam' },
          { name: 'item',   label: 'Item', type: 'datalist', fetchItems: true },
          { name: 'amount', label: 'Aantal', type: 'number', min: 1, max: 64, default: 1 },
        ],
      },
      { label: 'Clear inventory', type: 'form', template: 'clear {player}',
        fields: [{ name: 'player', label: 'Speler', type: 'text', placeholder: '' }],
      },
    ]},
    { label: 'FTB Essentials', cmds: [
      { label: 'Heal', type: 'form', template: 'heal {player}',
        fields: [{ name: 'player', label: 'Speler (leeg = jezelf)', type: 'text', placeholder: '' }],
      },
      { label: 'Feed', type: 'form', template: 'feed {player}',
        fields: [{ name: 'player', label: 'Speler (leeg = jezelf)', type: 'text', placeholder: '' }],
      },
      { label: 'Fly toggle', type: 'form', template: 'fly {player}',
        fields: [{ name: 'player', label: 'Speler (leeg = jezelf)', type: 'text', placeholder: '' }],
      },
      { label: 'God toggle', type: 'form', template: 'god {player}',
        fields: [{ name: 'player', label: 'Speler', type: 'text', placeholder: '' }],
      },
      { label: 'Invsee', type: 'form', template: 'invsee {player}',
        fields: [{ name: 'player', label: 'Speler', type: 'text', placeholder: '' }],
      },
      { label: 'Speed', type: 'form', template: 'speed {percent} {player}',
        fields: [
          { name: 'percent', label: 'Snelheid %', type: 'number', min: -100, max: 2000, default: 100 },
          { name: 'player',  label: 'Speler', type: 'text', placeholder: '' },
        ],
      },
      { label: 'Mute', type: 'form', template: 'mute {player} {duration}',
        fields: [
          { name: 'player',   label: 'Speler', type: 'text', placeholder: '' },
          { name: 'duration', label: 'Duur (bijv. 10m, 1h)', type: 'text', placeholder: '10m' },
        ],
      },
      { label: 'Near', type: 'form', template: 'near {radius}',
        fields: [{ name: 'radius', label: 'Straal in blokken', type: 'number', min: 1, max: 500, default: 50 }],
      },
    ]},
    { label: 'Whitelist', cmds: [
      { label: 'WL List',   cmd: 'whitelist list',   type: 'instant' },
      { label: 'WL On',     cmd: 'whitelist on',     type: 'instant' },
      { label: 'WL Off',    cmd: 'whitelist off',    type: 'instant' },
      { label: 'WL Reload', cmd: 'whitelist reload', type: 'instant' },
      { label: 'WL Add', type: 'form', template: 'whitelist add {player}',
        fields: [{ name: 'player', label: 'Speler', type: 'text', placeholder: '' }],
      },
      { label: 'WL Remove', type: 'form', template: 'whitelist remove {player}',
        fields: [{ name: 'player', label: 'Speler', type: 'text', placeholder: '' }],
      },
    ]},
    { label: 'Wereld', cmds: [
      { label: 'Dag',         cmd: 'time set day',   type: 'instant' },
      { label: 'Nacht',       cmd: 'time set night', type: 'instant' },
      { label: 'Helder weer', cmd: 'weather clear',  type: 'instant' },
      { label: 'Regen',       cmd: 'weather rain',   type: 'instant' },
      { label: 'Onweer',      cmd: 'weather thunder',type: 'instant' },
      { label: 'Difficulty', type: 'form', template: 'difficulty {level}',
        fields: [{ name: 'level', label: 'Moeilijkheid', type: 'select', options: [
          { value: 'peaceful', label: 'Peaceful' },
          { value: 'easy',     label: 'Easy' },
          { value: 'normal',   label: 'Normal' },
          { value: 'hard',     label: 'Hard' },
        ]}],
      },
      { label: 'Time set', type: 'form', template: 'time set {ticks}',
        fields: [{ name: 'ticks', label: 'Ticks (dag=1000, nacht=13000)', type: 'number', min: 0, max: 24000, default: 1000 }],
      },
      { label: 'World border', type: 'form', template: 'worldborder set {size}',
        fields: [{ name: 'size', label: 'Diameter (blokken)', type: 'number', min: 1, max: 60000000, default: 10000 }],
      },
      { label: 'Locate', type: 'form', template: 'locate structure {structure}',
        fields: [{ name: 'structure', label: 'Structuur', type: 'select', options: [
          'minecraft:village','minecraft:stronghold','minecraft:end_city',
          'minecraft:nether_fortress','minecraft:bastion_remnant','minecraft:monument',
          'minecraft:mansion','minecraft:mineshaft','minecraft:dungeon',
          'minecraft:desert_pyramid','minecraft:jungle_temple','minecraft:swamp_hut',
          'minecraft:igloo','minecraft:pillager_outpost','minecraft:ruined_portal',
          'minecraft:shipwreck','minecraft:buried_treasure','minecraft:ocean_ruin',
        ]}],
      },
    ]},
    { label: 'Gamerules', cmds: [
      { label: 'Keep Inventory', type: 'form', template: 'gamerule keepInventory {val}',
        fields: [{ name: 'val', label: 'Spullen bewaren bij dood', type: 'select', options: [{ value: 'true', label: 'Aan' }, { value: 'false', label: 'Uit' }] }],
      },
      { label: 'PvP', type: 'form', template: 'gamerule pvp {val}',
        fields: [{ name: 'val', label: 'Speler vs speler', type: 'select', options: [{ value: 'true', label: 'Aan' }, { value: 'false', label: 'Uit' }] }],
      },
      { label: 'Mob Griefing', type: 'form', template: 'gamerule mobGriefing {val}',
        fields: [{ name: 'val', label: 'Creepers/endermen vernielen', type: 'select', options: [{ value: 'true', label: 'Aan' }, { value: 'false', label: 'Uit' }] }],
      },
      { label: 'Spawn Monsters', type: 'form', template: 'gamerule doMobSpawning {val}',
        fields: [{ name: 'val', label: 'Monsters spawnen', type: 'select', options: [{ value: 'true', label: 'Aan' }, { value: 'false', label: 'Uit' }] }],
      },
      { label: 'Fire Spread', type: 'form', template: 'gamerule doFireTick {val}',
        fields: [{ name: 'val', label: 'Vuur verspreidt', type: 'select', options: [{ value: 'true', label: 'Aan' }, { value: 'false', label: 'Uit' }] }],
      },
      { label: 'Immediate Respawn', type: 'form', template: 'gamerule immediateRespawn {val}',
        fields: [{ name: 'val', label: 'Direct respawnen (geen scherm)', type: 'select', options: [{ value: 'true', label: 'Aan' }, { value: 'false', label: 'Uit' }] }],
      },
      { label: 'Day/Night cycle', type: 'form', template: 'gamerule doDaylightCycle {val}',
        fields: [{ name: 'val', label: 'Dag/nacht cyclus', type: 'select', options: [{ value: 'true', label: 'Aan' }, { value: 'false', label: 'Uit' }] }],
      },
      { label: 'Weather cycle', type: 'form', template: 'gamerule doWeatherCycle {val}',
        fields: [{ name: 'val', label: 'Weerverandering', type: 'select', options: [{ value: 'true', label: 'Aan' }, { value: 'false', label: 'Uit' }] }],
      },
      { label: 'Sleep %', type: 'form', template: 'gamerule playersSleepingPercentage {n}',
        fields: [{ name: 'n', label: '% spelers dat moet slapen', type: 'number', min: 0, max: 100, default: 100 }],
      },
      { label: 'Random Tick Speed', type: 'form', template: 'gamerule randomTickSpeed {n}',
        fields: [{ name: 'n', label: 'Speed (0=uit, standaard=3)', type: 'number', min: 0, max: 1000, default: 3 }],
      },
      { label: 'Fall Damage', type: 'form', template: 'gamerule fallDamage {val}',
        fields: [{ name: 'val', label: 'Valschade', type: 'select', options: [{ value: 'true', label: 'Aan' }, { value: 'false', label: 'Uit' }] }],
      },
    ]},
  ],

  cs16: [
    { label: 'Info', cmds: [
      { label: 'Status',       cmd: 'status',      type: 'instant' },
      { label: 'Users',        cmd: 'users',       type: 'instant' },
      { label: 'Stats',        cmd: 'stats',       type: 'instant' },
      { label: 'CVar list',    cmd: 'cvarlist',    type: 'instant' },
      { label: 'Say bericht', type: 'form', template: 'say {msg}',
        fields: [{ name: 'msg', label: 'Bericht in chat', type: 'text', placeholder: 'Ronde start over 30s' }],
      },
    ]},
    { label: 'Map', cmds: [
      { label: 'Restart match',  cmd: 'mp_restartgame 1', type: 'instant' },
      { label: 'Restart 3s',     cmd: 'mp_restartgame 3', type: 'instant' },
      { label: 'Wissel map', type: 'form', template: 'changelevel {map}',
        fields: [{ name: 'map', label: 'Kaart', type: 'select', options: [
          'de_dust2','de_inferno','de_nuke','de_train','de_aztec','de_cbble',
          'de_prodigy','de_airstrip','de_chateau','de_dust','de_cpl_mill',
          'de_cpl_strike','de_cpl_fire','de_russka','de_tuscan',
          'cs_office','cs_italy','cs_assault','cs_militia','cs_747',
          'cs_backalley','cs_siege','cs_havana','cs_estate','cs_thunder',
          'as_oilrig',
        ]}],
      },
      { label: 'Volgende map', cmd: 'nextmap', type: 'instant' },
      { label: 'Exec cfg', type: 'form', template: 'exec {file}',
        fields: [{ name: 'file', label: 'Config bestand', type: 'text', placeholder: 'server.cfg' }],
      },
    ]},
    { label: 'Ronde-regels', cmds: [
      { label: 'Timelimit', type: 'form', template: 'mp_timelimit {n}',
        fields: [{ name: 'n', label: 'Minuten per ronde', type: 'number', min: 0, max: 60, default: 30 }],
      },
      { label: 'Roundtime', type: 'form', template: 'mp_roundtime {n}',
        fields: [{ name: 'n', label: 'Minuten per round (1-9)', type: 'number', min: 1, max: 9, default: 2 }],
      },
      { label: 'Freezetime', type: 'form', template: 'mp_freezetime {n}',
        fields: [{ name: 'n', label: 'Seconden freeze', type: 'number', min: 0, max: 60, default: 6 }],
      },
      { label: 'Startgeld', type: 'form', template: 'mp_startmoney {n}',
        fields: [{ name: 'n', label: 'Startgeld ($)', type: 'number', min: 800, max: 16000, default: 800 }],
      },
      { label: 'Buytime', type: 'form', template: 'mp_buytime {n}',
        fields: [{ name: 'n', label: 'Minuten koopvenster', type: 'number', min: 0, max: 10, default: 1 }],
      },
      { label: 'C4-timer', type: 'form', template: 'mp_c4timer {n}',
        fields: [{ name: 'n', label: 'Seconden tot boem', type: 'number', min: 10, max: 90, default: 45 }],
      },
    ]},
    { label: 'Gameplay', cmds: [
      { label: 'Friendly fire', type: 'form', template: 'mp_friendlyfire {val}',
        fields: [{ name: 'val', label: 'Team damage', type: 'select', options: [{ value: '0', label: 'Uit' }, { value: '1', label: 'Aan' }] }],
      },
      { label: 'Autoteam balance', type: 'form', template: 'mp_autoteambalance {val}',
        fields: [{ name: 'val', label: 'Auto-balans', type: 'select', options: [{ value: '1', label: 'Aan' }, { value: '0', label: 'Uit' }] }],
      },
      { label: 'Limitteams', type: 'form', template: 'mp_limitteams {n}',
        fields: [{ name: 'n', label: 'Max verschil (0=uit)', type: 'number', min: 0, max: 10, default: 2 }],
      },
      { label: 'Chattime', type: 'form', template: 'mp_chattime {n}',
        fields: [{ name: 'n', label: 'Seconden na map-einde', type: 'number', min: 1, max: 60, default: 10 }],
      },
      { label: 'Set CVar', type: 'form', template: '{cvar} {value}',
        fields: [
          { name: 'cvar',  label: 'CVar naam', type: 'text', placeholder: 'mp_forcecamera' },
          { name: 'value', label: 'Waarde',    type: 'text', placeholder: '1' },
        ],
      },
    ]},
    { label: 'Spelers', cmds: [
      { label: 'Kick spelernaam', type: 'form', template: 'kick "{name}"',
        fields: [{ name: 'name', label: 'Spelernaam', type: 'text', placeholder: '' }],
      },
      { label: 'Kick userid', type: 'form', template: 'kick #{id}',
        fields: [{ name: 'id', label: 'User-ID (uit status)', type: 'number', min: 1 }],
      },
      { label: 'Ban userid (min)', type: 'form', template: 'banid {min} #{id} kick',
        fields: [
          { name: 'min', label: 'Duur in min (0=perm)', type: 'number', min: 0, default: 60 },
          { name: 'id',  label: 'User-ID',              type: 'number', min: 1 },
        ],
      },
      { label: 'Ban IP',   type: 'form', template: 'addip {min} {ip}',
        fields: [
          { name: 'min', label: 'Duur in min (0=perm)', type: 'number', min: 0, default: 60 },
          { name: 'ip',  label: 'IP-adres',              type: 'text' },
        ],
      },
    ]},
    { label: 'Server', cmds: [
      { label: 'Server-naam', type: 'form', template: 'hostname "{name}"',
        fields: [{ name: 'name', label: 'Hostnaam', type: 'text', placeholder: 'Bear & Bockie CS 1.6' }],
      },
      { label: 'Wachtwoord', type: 'form', template: 'sv_password {pw}',
        fields: [{ name: 'pw', label: 'Wachtwoord (leeg = geen)', type: 'text', placeholder: '' }],
      },
      { label: 'Max spelers', type: 'form', template: 'maxplayers {n}',
        fields: [{ name: 'n', label: 'Slots (na restart)', type: 'number', min: 2, max: 32, default: 16 }],
      },
      { label: 'Server-herstart', cmd: '_restart',   type: 'instant' },
      { label: 'Server-stop',     cmd: 'quit',        type: 'instant' },
    ]},
  ],

  teamspeak: [
    { label: 'Info', cmds: [
      { label: 'Who Am I',    cmd: 'whoami',                      type: 'instant' },
      { label: 'Version',     cmd: 'version',                     type: 'instant' },
      { label: 'Server Info', cmd: 'serverinfo',                  type: 'instant' },
      { label: 'Host Info',   cmd: 'hostinfo',                    type: 'instant' },
      { label: 'Conn. Info',  cmd: 'serverrequestconnectioninfo', type: 'instant' },
      { label: 'Server List', cmd: 'serverlist',                  type: 'instant' },
      { label: 'Log (50 regels)', cmd: 'logview lines=50',        type: 'instant' },
      { label: 'Log (aangepast)', type: 'form', template: 'logview lines={n}',
        fields: [{ name: 'n', label: 'Aantal regels', type: 'number', min: 1, max: 500, default: 100 }],
      },
    ]},
    { label: 'Clients', cmds: [
      { label: 'Client List', cmd: 'clientlist',                  type: 'instant' },
      { label: 'Client List (details)', cmd: 'clientlist -uid -away -voice -times -groups -info -country', type: 'instant' },
      { label: 'Client Info', type: 'form', template: 'clientinfo clid={clid}',
        fields: [{ name: 'clid', label: 'Client ID', type: 'number', min: 1, max: 9999, default: 1 }],
      },
      { label: 'Client zoeken', type: 'form', template: 'clientfind pattern={name}',
        fields: [{ name: 'name', label: 'Naam (deels)', type: 'text', placeholder: '' }],
      },
      { label: 'Kick (server)', type: 'form', template: 'clientkick clid={clid} reasonid=5 reasonmsg={msg}',
        fields: [
          { name: 'clid', label: 'Client ID', type: 'number', min: 1, max: 9999, default: 1 },
          { name: 'msg',  label: 'Reden', type: 'text', placeholder: 'Gekickt door admin' },
        ],
      },
      { label: 'Kick (kanaal)', type: 'form', template: 'clientkick clid={clid} reasonid=4 reasonmsg={msg}',
        fields: [
          { name: 'clid', label: 'Client ID', type: 'number', min: 1, max: 9999, default: 1 },
          { name: 'msg',  label: 'Reden', type: 'text', placeholder: '' },
        ],
      },
      { label: 'Move client', type: 'form', template: 'clientmove clid={clid} cid={cid}',
        fields: [
          { name: 'clid', label: 'Client ID',  type: 'number', min: 1, max: 9999, default: 1 },
          { name: 'cid',  label: 'Channel ID', type: 'number', min: 1, max: 9999, default: 1 },
        ],
      },
      { label: 'Poke', type: 'form', template: 'clientpoke clid={clid} msg={msg}',
        fields: [
          { name: 'clid', label: 'Client ID', type: 'number', min: 1, max: 9999, default: 1 },
          { name: 'msg',  label: 'Bericht', type: 'text', placeholder: '' },
        ],
      },
    ]},
    { label: 'Berichten', cmds: [
      { label: 'Global msg', type: 'form', template: 'gm msg={msg}',
        fields: [{ name: 'msg', label: 'Bericht (alle clients)', type: 'text', placeholder: '' }],
      },
      { label: 'Server msg', type: 'form', template: 'sendtextmessage targetmode=3 target=1 msg={msg}',
        fields: [{ name: 'msg', label: 'Bericht (server chat)', type: 'text', placeholder: '' }],
      },
      { label: 'Kanaal msg', type: 'form', template: 'sendtextmessage targetmode=2 target={cid} msg={msg}',
        fields: [
          { name: 'cid', label: 'Channel ID', type: 'number', min: 1, max: 9999, default: 1 },
          { name: 'msg', label: 'Bericht', type: 'text', placeholder: '' },
        ],
      },
      { label: 'Privé msg', type: 'form', template: 'sendtextmessage targetmode=1 target={clid} msg={msg}',
        fields: [
          { name: 'clid', label: 'Client ID', type: 'number', min: 1, max: 9999, default: 1 },
          { name: 'msg',  label: 'Bericht', type: 'text', placeholder: '' },
        ],
      },
    ]},
    { label: 'Kanalen', cmds: [
      { label: 'Channel List', cmd: 'channellist', type: 'instant' },
      { label: 'Channel Info', type: 'form', template: 'channelinfo cid={cid}',
        fields: [{ name: 'cid', label: 'Channel ID', type: 'number', min: 1, max: 9999, default: 1 }],
      },
      { label: 'Channel zoeken', type: 'form', template: 'channelfind pattern={name}',
        fields: [{ name: 'name', label: 'Naam (deels)', type: 'text', placeholder: '' }],
      },
      { label: 'Kanaal aanmaken', type: 'form', template: 'channelcreate channel_name={name} channel_flag_permanent={perm}',
        fields: [
          { name: 'name', label: 'Naam', type: 'text', placeholder: 'Gaming' },
          { name: 'perm', label: 'Permanent', type: 'select', options: [{ value: '1', label: 'Ja' }, { value: '0', label: 'Nee' }] },
        ],
      },
      { label: 'Kanaal verwijderen', type: 'form', template: 'channeldelete cid={cid} force=1',
        fields: [{ name: 'cid', label: 'Channel ID', type: 'number', min: 1, max: 9999, default: 1 }],
      },
    ]},
    { label: 'Bans', cmds: [
      { label: 'Ban List',    cmd: 'banlist',    type: 'instant' },
      { label: 'Ban All verwijderen', cmd: 'bandelall', type: 'instant' },
      { label: 'Ban client', type: 'form', template: 'banclient clid={clid} time={time} banreason={msg}',
        fields: [
          { name: 'clid', label: 'Client ID', type: 'number', min: 1, max: 9999, default: 1 },
          { name: 'time', label: 'Seconden (0=permanent)', type: 'number', min: 0, max: 86400, default: 0 },
          { name: 'msg',  label: 'Reden', type: 'text', placeholder: '' },
        ],
      },
      { label: 'Ban op naam', type: 'form', template: 'banadd name={name} time={time} banreason={msg}',
        fields: [
          { name: 'name', label: 'Naam (regex)', type: 'text', placeholder: 'SpamBot' },
          { name: 'time', label: 'Seconden (0=permanent)', type: 'number', min: 0, max: 86400, default: 0 },
          { name: 'msg',  label: 'Reden', type: 'text', placeholder: '' },
        ],
      },
      { label: 'Ban op IP', type: 'form', template: 'banadd ip={ip} time={time} banreason={msg}',
        fields: [
          { name: 'ip',   label: 'IP (regex)', type: 'text', placeholder: '192\\.168\\..*' },
          { name: 'time', label: 'Seconden (0=permanent)', type: 'number', min: 0, max: 86400, default: 0 },
          { name: 'msg',  label: 'Reden', type: 'text', placeholder: '' },
        ],
      },
      { label: 'Ban verwijderen', type: 'form', template: 'bandel banid={id}',
        fields: [{ name: 'id', label: 'Ban ID', type: 'number', min: 1, max: 9999, default: 1 }],
      },
    ]},
    { label: 'Groepen', cmds: [
      { label: 'Group List', cmd: 'servergrouplist', type: 'instant' },
      { label: 'Group aanmaken', type: 'form', template: 'servergroupadd name={name}',
        fields: [{ name: 'name', label: 'Groepsnaam', type: 'text', placeholder: 'Moderator' }],
      },
      { label: 'Client in groep', type: 'form', template: 'servergroupaddclient sgid={sgid} cldbid={cldbid}',
        fields: [
          { name: 'sgid',   label: 'Group ID',   type: 'number', min: 1, max: 9999, default: 1 },
          { name: 'cldbid', label: 'Client DB ID', type: 'number', min: 1, max: 99999, default: 1 },
        ],
      },
      { label: 'Client uit groep', type: 'form', template: 'servergroupdelclient sgid={sgid} cldbid={cldbid}',
        fields: [
          { name: 'sgid',   label: 'Group ID',   type: 'number', min: 1, max: 9999, default: 1 },
          { name: 'cldbid', label: 'Client DB ID', type: 'number', min: 1, max: 99999, default: 1 },
        ],
      },
    ]},
  ],
}

const getPresets = (eggName) => {
  const e = (eggName || '').toLowerCase()
  if (e.includes('minecraft') || e.includes('forge') || e.includes('neoforge') || e.includes('paper') || e.includes('vanilla') || e.includes('atm') || e.includes('mons')) return PRESETS.minecraft
  if (e.includes('quake') || e.includes('q3')) return PRESETS.quake3
  if (e.includes('mohaa') || e.includes('medal of honor') || e.includes('allied assault')) return PRESETS.mohaa
  if (e.includes('unreal') || e.includes('ut2004') || e.includes('ut200')) return PRESETS.ut2004
  if (e.includes('openra') || e.includes('open ra')) return PRESETS.openra
  if (e.includes('teamspeak') || e.includes('ts3')) return PRESETS.teamspeak
  // CS 1.6 / GoldSrc — matcht "Custom ReHLDS Engine Game", "CS 1.6",
  // "Counter-Strike", "Half-Life", en alles wat op "hlds" duidt.
  if (e.includes('rehlds') || e.includes('hlds') || e.includes('goldsrc')
      || e.includes('cs 1.6') || e.includes('cs1.6') || e.includes('cstrike')
      || e.includes('counter-strike') || e.includes('half-life')) return PRESETS.cs16
  return null
}

// ── Helpers ───────────────────────────────────────────────────────────────────

const initValues = (fields) =>
  Object.fromEntries(fields.map(f => [
    f.name,
    f.default !== undefined ? String(f.default)
      : f.type === 'select' ? (typeof f.options[0] === 'object' ? f.options[0].value : f.options[0])
      : f.type === 'number' ? '1'
      : '',
  ]))

const assembleCommand = (template, values, fields) =>
  template.replace(/\{(\w+)\}/g, (_, key) => {
    const val = values[key] ?? ''
    if (!val.trim()) {
      const field = fields.find(f => f.name === key)
      return `[${field?.label ?? key}]`
    }
    return val
  })

const isComplete = (template, values) =>
  !template.replace(/\{(\w+)\}/g, (_, k) => values[k] ?? '').includes('[')

// ── Status styling ─────────────────────────────────────────────────────────────
const STATUS_COLOR = {
  running:  'bg-green-500/20 text-green-400 border-green-500/30',
  starting: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30',
  stopping: 'bg-orange-500/20 text-orange-400 border-orange-500/30',
  offline:  'bg-red-500/20 text-red-400 border-red-500/30',
  unknown:  'bg-zinc-500/20 text-zinc-400 border-zinc-500/30',
}

// ── Mod → Item two-step selector ─────────────────────────────────────────────
function ModItemSelector({ value, onChange, items, loading, error, onRetry }) {
  const mods = useMemo(() => [...new Set(items.map(i => i.split(':')[0]))].sort(), [items])

  const initMod  = value?.includes(':') ? value.split(':')[0] : 'minecraft'
  const initItem = value?.includes(':') ? value.split(':').slice(1).join(':') : ''

  const [selMod,    setSelMod]    = useState(initMod)
  const [itemSearch, setItemSearch] = useState('')

  const modItems = useMemo(
    () => items.filter(i => i.startsWith(selMod + ':')).map(i => i.slice(selMod.length + 1)).sort(),
    [items, selMod]
  )

  const filteredItems = useMemo(() => {
    if (!itemSearch.trim()) return modItems
    const q = itemSearch.toLowerCase()
    return modItems.filter(i => i.includes(q))
  }, [modItems, itemSearch])

  // Current item value — default to first available when mod changes
  const [selItem, setSelItem] = useState(initItem)

  useEffect(() => {
    if (modItems.length && !modItems.includes(selItem)) {
      setSelItem(modItems[0])
      onChange(`${selMod}:${modItems[0]}`)
    }
  }, [selMod, modItems])

  const handleModChange = (mod) => {
    setSelMod(mod)
    setItemSearch('')
  }

  const handleItemChange = (item) => {
    setSelItem(item)
    onChange(`${selMod}:${item}`)
  }

  if (loading) return (
    <div className="flex-1 flex items-center gap-2 text-xs text-muted-foreground">
      <Loader2 className="h-3.5 w-3.5 animate-spin" /> Items laden van server (kan ~5s duren)...
    </div>
  )

  if (error) return (
    <div className="flex-1 flex items-center gap-2 text-xs text-red-400">
      Laden mislukt.
      <button onClick={onRetry} className="underline hover:text-red-300 transition-colors">Opnieuw proberen</button>
    </div>
  )

  if (!items.length) return (
    <div className="flex-1 flex items-center gap-2 text-xs text-muted-foreground">
      Nog geen items geladen.
      {onRetry && <button onClick={onRetry} className="underline hover:text-foreground transition-colors">Laden</button>}
    </div>
  )

  return (
    <div className="flex-1 flex flex-col gap-1.5">
      {/* Row 1: mod picker */}
      <div className="flex items-center gap-2">
        <span className="text-[10px] text-muted-foreground w-8 shrink-0">Mod</span>
        <select value={selMod} onChange={e => handleModChange(e.target.value)}
          className="flex-1 bg-background border border-border rounded px-2 py-1 text-xs font-mono text-foreground focus:outline-none focus:border-violet-500/50">
          {mods.map(m => <option key={m} value={m}>{m}</option>)}
        </select>
        <span className="text-[10px] text-muted-foreground shrink-0">{modItems.length} items</span>
      </div>
      {/* Row 2: item search + item picker */}
      <div className="flex items-center gap-2">
        <span className="text-[10px] text-muted-foreground w-8 shrink-0">Item</span>
        <input
          type="text"
          value={itemSearch}
          onChange={e => setItemSearch(e.target.value)}
          placeholder="filter..."
          className="w-24 shrink-0 bg-background border border-border rounded px-2 py-1 text-xs text-foreground placeholder:text-muted-foreground focus:outline-none focus:border-violet-500/50"
        />
        <select value={selItem} onChange={e => handleItemChange(e.target.value)}
          className="flex-1 bg-background border border-border rounded px-2 py-1 text-xs font-mono text-foreground focus:outline-none focus:border-violet-500/50">
          {filteredItems.map(i => <option key={i} value={i}>{i}</option>)}
          {filteredItems.length === 0 && <option disabled>Geen resultaten</option>}
        </select>
      </div>
    </div>
  )
}

// ── Command Builder ───────────────────────────────────────────────────────────
function CommandBuilder({ preset, onSend, onClose, sending, mcItems, mcItemsLoading, mcItemsError, onRetryMcItems }) {
  const [values, setValues] = useState(() => initValues(preset.fields))

  const preview = assembleCommand(preset.template, values, preset.fields)
  const ready   = isComplete(preset.template, values)
  const set     = (name, val) => setValues(v => ({ ...v, [name]: val }))

  const handleSubmit = (e) => {
    e.preventDefault()
    if (!ready || sending) return
    onSend(preview)
  }

  return (
    <div className="border border-violet-500/25 rounded-lg bg-violet-500/5 p-4 mb-3">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Sliders className="h-4 w-4 text-violet-400" />
          <span className="text-sm font-medium text-violet-300">{preset.label}</span>
        </div>
        <button onClick={onClose} className="text-muted-foreground hover:text-foreground transition-colors">
          <X className="h-4 w-4" />
        </button>
      </div>

      <form onSubmit={handleSubmit} className="space-y-3">
        {preset.fields.map(field => (
          <div key={field.name} className="flex items-start gap-3">
            <label className="text-xs text-muted-foreground w-36 shrink-0 text-right pt-2">{field.label}</label>
            {field.type === 'select' ? (
              <select value={values[field.name]} onChange={e => set(field.name, e.target.value)}
                className="flex-1 bg-background border border-border rounded px-2.5 py-1.5 text-sm text-foreground focus:outline-none focus:border-violet-500/50 focus:ring-1 focus:ring-violet-500/20">
                {field.options.map(opt =>
                  typeof opt === 'object'
                    ? <option key={opt.value} value={opt.value}>{opt.label}</option>
                    : <option key={opt} value={opt}>{opt}</option>
                )}
              </select>
            ) : field.type === 'number' ? (
              <input type="number" min={field.min} max={field.max} value={values[field.name]}
                onChange={e => set(field.name, e.target.value)}
                className="flex-1 bg-background border border-border rounded px-2.5 py-1.5 text-sm text-foreground focus:outline-none focus:border-violet-500/50 focus:ring-1 focus:ring-violet-500/20" />
            ) : field.type === 'datalist' ? (
              <ModItemSelector
                value={values[field.name]}
                onChange={val => set(field.name, val)}
                items={mcItems}
                loading={mcItemsLoading}
                error={mcItemsError}
                onRetry={onRetryMcItems}
              />
            ) : (
              <input type="text" value={values[field.name]} onChange={e => set(field.name, e.target.value)}
                placeholder={field.placeholder ?? ''}
                className="flex-1 bg-background border border-border rounded px-2.5 py-1.5 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:border-violet-500/50 focus:ring-1 focus:ring-violet-500/20" />
            )}
          </div>
        ))}

        {/* Preview + send */}
        <div className="flex items-center gap-2 pt-1 border-t border-border">
          <code className={`flex-1 text-xs px-3 py-1.5 rounded font-mono truncate ${ready ? 'bg-background text-foreground' : 'bg-background text-muted-foreground'}`}>
            {preview}
          </code>
          <button type="submit" disabled={!ready || sending}
            className="flex items-center gap-1.5 px-4 py-1.5 rounded bg-violet-500/20 text-violet-300 border border-violet-500/30 hover:bg-violet-500/30 disabled:opacity-40 disabled:cursor-not-allowed font-medium text-sm transition-colors shrink-0">
            <Send className="h-3.5 w-3.5" />
            {sending ? '...' : 'Verstuur'}
          </button>
        </div>
      </form>
    </div>
  )
}

// ── Main page ─────────────────────────────────────────────────────────────────
export default function GameConsolePage() {
  const [searchParams, setSearchParams] = useSearchParams()
  const initialServer = searchParams.get('server')
  const [servers, setServers]     = useState([])
  const [loading, setLoading]     = useState(true)
  const [selectedId, setSelectedId] = useState(initialServer || null)
  const [input, setInput]         = useState('')
  const [history, setHistory]     = useState([])
  const [sending, setSending]     = useState(false)
  const [powering, setPowering]       = useState(false)
  const [activeForm, setActiveForm]   = useState(null)   // { preset, key }
  const [mcItems, setMcItems]         = useState([])
  const [mcItemsLoading, setMcItemsLoading] = useState(false)
  const [mcItemsError, setMcItemsError]     = useState(false)
  const inputRef = useRef(null)

  useEffect(() => {
    pterodactylAPI.getServers(true)
      .then(r => {
        const list = r.data.servers || []
        setServers(list)
        // Als ?server=<identifier> in de URL zit én die server bestaat,
        // laat 'm geselecteerd. Anders val terug op de eerste server.
        setSelectedId(prev => {
          if (prev && list.some(s => s.identifier === prev)) return prev
          return list[0]?.identifier || null
        })
      })
      .catch(() => {})
      .finally(() => setLoading(false))
  }, [])

  // Clear active form when switching servers
  useEffect(() => { setActiveForm(null) }, [selectedId])

  // Houd de URL-query-param in sync met de geselecteerde server zodat een
  // gedeelde link naar deze pagina meteen op de juiste server opent.
  useEffect(() => {
    if (!selectedId) return
    const cur = searchParams.get('server')
    if (cur !== selectedId) {
      const next = new URLSearchParams(searchParams)
      next.set('server', selectedId)
      setSearchParams(next, { replace: true })
    }
  }, [selectedId])

  const selected = servers.find(s => s.identifier === selectedId) || null
  const presets  = selected ? getPresets(selected.egg_name) : null

  const loadMcItems = (identifier) => {
    if (mcItemsLoading) return
    setMcItemsLoading(true)
    setMcItemsError(false)
    pterodactylAPI.getMinecraftItems(identifier)
      .then(r => setMcItems(r.data.items || []))
      .catch(() => setMcItemsError(true))
      .finally(() => setMcItemsLoading(false))
  }

  // Pre-load Minecraft item list as soon as a MC server is selected
  useEffect(() => {
    if (!selected) return
    const isMc = (selected.egg_name || '').toLowerCase().match(/minecraft|forge|neoforge|paper|vanilla|atm|mons/)
    if (!isMc || mcItems.length > 0 || mcItemsLoading) return
    loadMcItems(selected.identifier)
  }, [selected?.identifier])

  const addHistory = (cmd, status) =>
    setHistory(h => [{ cmd, status, ts: new Date().toLocaleTimeString('nl-NL', { hour: '2-digit', minute: '2-digit', second: '2-digit' }) }, ...h].slice(0, 40))

  const sendCmd = async (cmd) => {
    if (!cmd.trim() || !selectedId || sending) return
    setSending(true)
    try {
      await pterodactylAPI.sendCommand(selectedId, cmd.trim())
      addHistory(cmd.trim(), 'ok')
    } catch {
      addHistory(cmd.trim(), 'err')
    } finally {
      setSending(false)
    }
  }

  const handlePresetClick = (c) => {
    if (c.type === 'instant') {
      sendCmd(c.cmd)
    } else if (c.type === 'fill') {
      setActiveForm(null)
      setInput(c.cmd)
      inputRef.current?.focus()
    } else if (c.type === 'form') {
      // Toggle: clicking same button closes the builder
      setActiveForm(prev => prev?.key === c.template ? null : { preset: c, key: c.template })
    }
  }

  const handleBuilderSend = (cmd) => {
    sendCmd(cmd)
    setActiveForm(null)
  }

  const handleSubmit = (e) => {
    e.preventDefault()
    if (!input.trim()) return
    sendCmd(input)
    setInput('')
  }

  const handlePower = async (signal) => {
    if (!selectedId || powering) return
    setPowering(true)
    try {
      await pterodactylAPI.sendPower(selectedId, signal)
      addHistory(`[power] ${signal}`, 'ok')
      setTimeout(() => {
        pterodactylAPI.getServers(true).then(r => setServers(r.data.servers || [])).catch(() => {})
      }, 3000)
    } catch {
      addHistory(`[power] ${signal}`, 'err')
    } finally {
      setPowering(false)
    }
  }

  if (loading) return (
    <div className="flex items-center justify-center h-64 text-muted-foreground">
      <Terminal className="h-5 w-5 mr-2 animate-pulse" /> Servers laden...
    </div>
  )

  return (
    <div className="flex h-[calc(100vh-4rem)] overflow-hidden">

      {/* ── Server list sidebar ── */}
      <div className="w-52 shrink-0 border-r border-border bg-card flex flex-col overflow-hidden">
        <div className="px-3 py-3 border-b border-border">
          <h2 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground flex items-center gap-1.5">
            <Terminal className="h-3.5 w-3.5" /> Servers
          </h2>
        </div>
        <div className="flex-1 overflow-y-auto py-1">
          {servers.map(s => {
            const sk = s.status in STATUS_COLOR ? s.status : 'unknown'
            const dot = { running:'bg-green-500', starting:'bg-yellow-500', stopping:'bg-orange-500', offline:'bg-red-500', unknown:'bg-zinc-500' }[sk]
            return (
              <button key={s.identifier} onClick={() => setSelectedId(s.identifier)}
                className={`w-full text-left px-3 py-2.5 flex items-start gap-2 transition-colors text-sm ${
                  selectedId === s.identifier
                    ? 'bg-neon-blue/10 text-neon-blue border-r-2 border-neon-blue'
                    : 'text-muted-foreground hover:text-foreground hover:bg-accent'
                }`}
              >
                <span className={`mt-1.5 h-2 w-2 rounded-full shrink-0 ${dot}`} />
                <div className="min-w-0">
                  <p className="truncate font-medium leading-tight">{s.name}</p>
                  <p className="text-[10px] opacity-60 truncate">{s.egg_name || 'Onbekend'}</p>
                </div>
              </button>
            )
          })}
        </div>
      </div>

      {/* ── Main panel ── */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {!selected ? (
          <div className="flex-1 flex items-center justify-center text-muted-foreground">Selecteer een server</div>
        ) : (
          <>
            {/* Server header */}
            <div className="shrink-0 px-5 py-3 border-b border-border bg-card flex items-center justify-between gap-4">
              <div className="flex items-center gap-3 min-w-0">
                <Terminal className="h-5 w-5 text-neon-blue shrink-0" />
                <div className="min-w-0">
                  <h1 className="font-semibold text-foreground truncate">{selected.name}</h1>
                  <p className="text-xs text-muted-foreground">{selected.egg_name} · {selected.ip}:{selected.port}</p>
                </div>
                <span className={`shrink-0 text-xs px-2 py-0.5 rounded border font-medium capitalize ${STATUS_COLOR[selected.status in STATUS_COLOR ? selected.status : 'unknown']}`}>
                  {selected.status}
                </span>
              </div>
              <div className="flex gap-2 shrink-0">
                <button onClick={() => handlePower('start')} disabled={powering || selected.status === 'running'}
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded text-xs font-medium bg-green-500/10 text-green-400 border border-green-500/30 hover:bg-green-500/20 disabled:opacity-40 disabled:cursor-not-allowed transition-colors">
                  <Play className="h-3.5 w-3.5" /> Start
                </button>
                <button onClick={() => handlePower('restart')} disabled={powering}
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded text-xs font-medium bg-yellow-500/10 text-yellow-400 border border-yellow-500/30 hover:bg-yellow-500/20 disabled:opacity-40 disabled:cursor-not-allowed transition-colors">
                  <RotateCcw className="h-3.5 w-3.5" /> Restart
                </button>
                <button onClick={() => handlePower('stop')} disabled={powering || selected.status === 'offline'}
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded text-xs font-medium bg-red-500/10 text-red-400 border border-red-500/30 hover:bg-red-500/20 disabled:opacity-40 disabled:cursor-not-allowed transition-colors">
                  <Square className="h-3.5 w-3.5" /> Stop
                </button>
              </div>
            </div>

            <div className="flex-1 flex overflow-hidden">
              {/* ── Command palette ── */}
              <div className="w-64 shrink-0 border-r border-border overflow-y-auto bg-card/50 p-3 flex flex-col gap-4">
                {presets ? presets.map(section => (
                  <div key={section.label}>
                    <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground mb-1.5 px-1">{section.label}</p>
                    <div className="flex flex-col gap-1">
                      {section.cmds.map(c => {
                        const isActive = activeForm?.key === c.template
                        const colors = c.type === 'instant'
                          ? 'bg-neon-blue/10 text-neon-blue border-neon-blue/25 hover:bg-neon-blue/20'
                          : c.type === 'form'
                            ? isActive
                              ? 'bg-violet-500/25 text-violet-200 border-violet-400/50'
                              : 'bg-violet-500/10 text-violet-300 border-violet-500/25 hover:bg-violet-500/20'
                            : 'bg-zinc-800/60 text-zinc-300 border-zinc-700 hover:bg-zinc-700/60'
                        const Icon = c.type === 'instant' ? Zap : c.type === 'form' ? Sliders : Type
                        return (
                          <button key={c.template ?? c.cmd} onClick={() => handlePresetClick(c)}
                            disabled={sending && c.type === 'instant'}
                            className={`w-full flex items-center gap-2 px-2.5 py-1.5 rounded text-xs font-medium border transition-colors disabled:opacity-50 disabled:cursor-not-allowed text-left ${colors}`}
                          >
                            <Icon className="h-3 w-3 shrink-0" />
                            <span className="flex-1">{c.label}</span>
                          </button>
                        )
                      })}
                    </div>
                  </div>
                )) : (
                  <p className="text-xs text-muted-foreground px-1">Geen presets voor {selected.egg_name || 'dit spel'}. Gebruik het vrij invoerveld.</p>
                )}
              </div>

              {/* ── Right: builder + input + history ── */}
              <div className="flex-1 flex flex-col overflow-hidden">
                <div className="shrink-0 p-4 border-b border-border">
                  {/* Command builder */}
                  {activeForm && (
                    <CommandBuilder
                      preset={activeForm.preset}
                      sending={sending}
                      onSend={handleBuilderSend}
                      onClose={() => setActiveForm(null)}
                      mcItems={mcItems}
                      mcItemsLoading={mcItemsLoading}
                      mcItemsError={mcItemsError}
                      onRetryMcItems={() => loadMcItems(selected.identifier)}
                    />
                  )}

                  {/* Free input */}
                  <form onSubmit={handleSubmit} className="flex gap-2">
                    <div className="flex-1 relative">
                      <ChevronRight className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-neon-blue/60" />
                      <input ref={inputRef} value={input} onChange={e => setInput(e.target.value)}
                        placeholder="Vrij commando invoeren..."
                        disabled={sending}
                        className="w-full bg-background border border-border rounded pl-9 pr-4 py-2 text-sm font-mono text-foreground placeholder:text-muted-foreground focus:outline-none focus:border-neon-blue/50 focus:ring-1 focus:ring-neon-blue/20 disabled:opacity-50"
                      />
                    </div>
                    <button type="submit" disabled={sending || !input.trim()}
                      className="flex items-center gap-2 px-4 py-2 rounded bg-neon-blue/20 text-neon-blue border border-neon-blue/30 hover:bg-neon-blue/30 disabled:opacity-40 disabled:cursor-not-allowed font-medium text-sm transition-colors">
                      <Send className="h-4 w-4" />
                      {sending ? '...' : 'Send'}
                    </button>
                  </form>
                  <p className="mt-1.5 text-[10px] text-muted-foreground flex gap-3">
                    <span><span className="text-neon-blue/70">⚡ Blauw</span> = direct</span>
                    <span><span className="text-violet-400">⊞ Paars</span> = invulformulier</span>
                    <span><span className="text-zinc-400">✎ Grijs</span> = in veld</span>
                  </p>
                </div>

                {/* History */}
                <div className="flex-1 overflow-y-auto p-4 font-mono text-xs space-y-1">
                  {history.length === 0
                    ? <p className="text-muted-foreground text-center py-8">Nog geen commando's verstuurd.</p>
                    : history.map((h, i) => (
                      <div key={i} onClick={() => { setInput(h.cmd); inputRef.current?.focus() }}
                        className="flex items-center gap-2 group hover:bg-accent/30 rounded px-2 py-1 cursor-pointer"
                        title="Klik om opnieuw in te vullen"
                      >
                        <span className="text-muted-foreground shrink-0 w-16">{h.ts}</span>
                        <span className={h.status === 'ok' ? 'text-green-400' : 'text-red-400'}>{h.status === 'ok' ? '✓' : '✗'}</span>
                        <span className="text-foreground/80 truncate flex-1">{h.cmd}</span>
                        <span className="opacity-0 group-hover:opacity-50 text-muted-foreground shrink-0 text-[10px]">↑ opnieuw</span>
                      </div>
                    ))
                  }
                </div>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  )
}
